import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { examService, questionService, sourceService } from '../services/api';
import './ExamConfig.css';

function ExamConfig() {
  const navigate = useNavigate();
  const [config, setConfig] = useState({
    title: 'Simulado SAP Commerce Cloud',
    examMode: 'simulation',
    correctionMode: 'end',
    totalQuestions: 80,
    sourceFilter: 'all' // all, dumps, learning_journeys, misc, or specific dump name
  });
  const [creating, setCreating] = useState(false);
  const [loadingAnswerKey, setLoadingAnswerKey] = useState(false);
  const [totalAvailable, setTotalAvailable] = useState(80);
  const [loading, setLoading] = useState(true);
  const [dumps, setDumps] = useState([]);
  const [learningJourneys, setLearningJourneys] = useState([]);
  const [aiModel, setAiModel] = useState(
    localStorage.getItem('aiModel') || 'qwen2.5:7b'
  );

  // Carregar dumps disponíveis
  useEffect(() => {
    const fetchSources = async () => {
      try {
        const dumpsData = await sourceService.getDumps();
        setDumps(dumpsData);
        const learningJourneysData = await sourceService.getSourcesByType('learning_journey');
        setLearningJourneys(learningJourneysData);
      } catch (error) {
        console.error('Error fetching sources:', error);
      }
    };
    fetchSources();
  }, []);

  useEffect(() => {
    const fetchQuestionCount = async () => {
      try {
        let count;
        const sourceFilter = config.sourceFilter;
        const isSourceType = ['all', 'dump', 'learning_journey', 'misc'].includes(sourceFilter);
        if (isSourceType) {
          const sourceType = sourceFilter === 'all' ? null : sourceFilter;
          count = await questionService.countQuestions(sourceType);
        } else {
          count = await questionService.countQuestions(null, sourceFilter);
        }
        setTotalAvailable(count);
        setConfig(prev => ({ ...prev, totalQuestions: Math.min(prev.totalQuestions, count) }));
      } catch (error) {
        console.error('Error fetching question count:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchQuestionCount();
  }, [config.sourceFilter]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setCreating(true);

    try {
      localStorage.setItem('aiModel', aiModel);
      const exam = await examService.createExam(config);
      console.log('Exam created:', exam);
      navigate(`/exam/${exam.id}`);
    } catch (error) {
      console.error('Error creating exam:', error);
      alert('Erro ao criar simulado. Verifique se o backend está rodando.');
      setCreating(false);
    }
  };

  const handleViewAnswerKey = async () => {
    setLoadingAnswerKey(true);

    try {
      // Load questions based on source filter (similar to ExamPage logic)
      let allQuestions;
      if (config.sourceFilter && config.sourceFilter !== 'all') {
        const isSourceType = ['dump', 'learning_journey', 'misc'].includes(config.sourceFilter);
        if (isSourceType) {
          allQuestions = await questionService.getQuestionsBySourceType(config.sourceFilter);
        } else {
          allQuestions = await questionService.getQuestionsBySourceName(config.sourceFilter);
        }
      } else {
        allQuestions = await questionService.getAllQuestions();
      }

      // Order by externalId or id
      const ordered = [...allQuestions].sort((a, b) => {
        const aKey = a.externalId ?? a.id ?? 0;
        const bKey = b.externalId ?? b.id ?? 0;
        return aKey - bKey;
      });

      // Shuffle if source filter is general type
      const shouldShuffle = ['all', 'dump', 'learning_journey'].includes(config.sourceFilter);
      const finalQuestions = shouldShuffle
        ? ordered.sort(() => 0.5 - Math.random())
        : ordered;

      // Select first N questions
      const selectedQuestions = finalQuestions.slice(0, config.totalQuestions);

      // Navigate to answer key page with questions
      navigate('/answer-key', {
        state: {
          questions: selectedQuestions,
          examTitle: config.title
        }
      });
    } catch (error) {
      console.error('Error loading answer key:', error);
      alert('Erro ao carregar gabarito. Verifique se o backend está rodando.');
    } finally {
      setLoadingAnswerKey(false);
    }
  };

  return (
    <div className="exam-config-container">
      <div className="container">
        <div className="card">
          <h1>⚙️ Configurar Simulado</h1>

          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label>Fonte das Questões</label>
              <select
                value={config.sourceFilter}
                onChange={(e) => setConfig({...config, sourceFilter: e.target.value})}
                className="form-control"
              >
                <option value="all">📚 Todas as fontes (Recomendado)</option>
                <option value="dump">📄 Todos os Dumps juntos (Provas reais)</option>

                {/* Dumps individuais */}
                {dumps.length > 0 && (
                  <optgroup label="📄 Dumps Específicos">
                    {dumps.map(dump => (
                      <option key={dump.id} value={dump.name}>
                        {dump.name} - {dump.description || 'Dump de prova real'}
                      </option>
                    ))}
                  </optgroup>
                )}

                <option value="learning_journey">🎓 Todos os Learning Journeys juntos</option>

                {learningJourneys.length > 0 && (
                  <optgroup label="🎓 Learning Journeys Específicos">
                    {learningJourneys.map(source => (
                      <option key={source.id} value={source.name}>
                        {source.name} - {source.description || 'Learning Journey'}
                      </option>
                    ))}
                  </optgroup>
                )}
                <option value="misc">📝 Questionários Diversos</option>
              </select>
            </div>

            <div className="form-group">
              <label>Modelo de IA</label>
              <select
                value={aiModel}
                onChange={(e) => setAiModel(e.target.value)}
                className="form-control"
              >
                <option value="qwen2.5:7b">Qwen2.5 7B (Recomendado)</option>
                <option value="llama3.1:8b">Llama 3.1 8B</option>
              </select>
            </div>

            <div className="form-group">
              <label>Modo de Correção</label>
              <select
                value={config.correctionMode}
                onChange={(e) => setConfig({...config, correctionMode: e.target.value})}
                className="form-control"
              >
                <option value="end">📊 Ao final do simulado</option>
                <option value="immediate">⚡ Imediata (após cada questão)</option>
              </select>
            </div>

            <div className="form-group">
              <label>Número de Questões {loading ? '(Carregando...)' : `(Total disponível: ${totalAvailable})`}</label>
              <input
                type="number"
                min="1"
                max={totalAvailable}
                value={config.totalQuestions}
                onChange={(e) => setConfig({...config, totalQuestions: parseInt(e.target.value)})}
                className="form-control"
                disabled={loading}
              />
            </div>

            <div className="button-group">
              <button type="button" className="btn btn-secondary" onClick={() => navigate('/')}>
                Voltar
              </button>
              <button
                type="button"
                className="btn btn-success"
                onClick={handleViewAnswerKey}
                disabled={loadingAnswerKey || loading}
              >
                {loadingAnswerKey ? 'Carregando...' : '📋 Ver Gabarito'}
              </button>
              <button type="submit" className="btn btn-primary" disabled={creating}>
                {creating ? 'Criando...' : 'Iniciar Simulado'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default ExamConfig;
